import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Inject,
  PLATFORM_ID,
} from '@angular/core';

interface SuitItem {
  symbol: string;
  x: string;
  y: string;
  delay: string;
  size: string;
}

interface StarParticle {
  left: string;
  top: string;
  size: number;
  duration: number;
  delay: number;
}

@Component({
  selector: 'app-splash-screen',
  imports: [CommonModule],
  standalone: true,
  templateUrl: './splash-screen.component.html',
  styleUrl: './splash-screen.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SplashScreenComponent implements OnInit, OnDestroy {
  @Output() finished = new EventEmitter<void>();

  progress: number = 0;
  statusText: string = 'Initializing LuckyCrest Vault...';
  isVisible: boolean = true;
  isExiting: boolean = false;

  readonly casinoSuits: SuitItem[] = [
    { symbol: '♠', x: '12%', y: '22%', delay: '0s', size: '26px' },
    { symbol: '♦', x: '86%', y: '26%', delay: '0.7s', size: '24px' },
    { symbol: '♣', x: '15%', y: '74%', delay: '1.4s', size: '28px' },
    { symbol: '♥', x: '84%', y: '76%', delay: '0.4s', size: '26px' },
    { symbol: '★', x: '50%', y: '10%', delay: '1.1s', size: '20px' },
    { symbol: '✦', x: '78%', y: '50%', delay: '1.8s', size: '18px' },
    { symbol: '✦', x: '22%', y: '48%', delay: '2.1s', size: '18px' },
  ];

  readonly stars: StarParticle[] = Array.from({ length: 26 }, () => ({
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    size: Math.random() * 3 + 1.5,
    duration: 1800 + Math.random() * 2200,
    delay: Math.random() * 1500,
  }));

  private progressTimer?: any;
  private exitTimer?: any;
  private readonly targetDurationMs = 1700; // time to reach 100%
  private readonly exitDurationMs = 450;    // exit fade-out duration

  constructor(
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      this.progress = 100;
      return;
    }
    this.startSmoothLoading();
  }

  private startSmoothLoading(): void {
    if (typeof window === 'undefined' || typeof requestAnimationFrame === 'undefined') {
      this.progress = 100;
      this.handleComplete();
      return;
    }

    const startTime = performance.now();

    const step = () => {
      const elapsed = performance.now() - startTime;
      const progressRatio = Math.min(elapsed / this.targetDurationMs, 1);

      // Smooth ease-out cubic
      const eased = 1 - Math.pow(1 - progressRatio, 2.5);
      this.progress = Math.min(Math.round(eased * 100), 100);

      this.updateStatusText(this.progress);
      this.cdr.markForCheck();

      if (progressRatio < 1) {
        this.progressTimer = requestAnimationFrame(step);
      } else {
        this.handleComplete();
      }
    };

    this.progressTimer = requestAnimationFrame(step);
  }

  private updateStatusText(p: number): void {
    if (p < 25) {
      this.statusText = 'Initializing LuckyCrest Vault...';
    } else if (p < 55) {
      this.statusText = 'Loading Real-Time Tables & Games...';
    } else if (p < 85) {
      this.statusText = 'Securing VIP Rewards & Wallets...';
    } else if (p < 100) {
      this.statusText = 'Finalizing Casino Lobby...';
    } else {
      this.statusText = 'Welcome to LuckyCrest!';
    }
  }

  private handleComplete(): void {
    this.isExiting = true;
    this.cdr.markForCheck();

    this.exitTimer = setTimeout(() => {
      this.isVisible = false;
      this.finished.emit();
      this.cdr.markForCheck();
    }, this.exitDurationMs);
  }

  trackByIndex(index: number): number {
    return index;
  }

  ngOnDestroy(): void {
    if (this.progressTimer && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.progressTimer);
    }
    if (this.exitTimer) {
      clearTimeout(this.exitTimer);
    }
  }
}
